import React, { useState, useEffect, useRef } from 'react';
import {
  VStack,
  HStack,
  Text,
  Box,
  Pressable,
  Icon,
  Heading,
  Badge,
  BadgeText,
  Center,
} from '@gluestack-ui/themed';
import {
  ChevronDown, ChevronRight, User, FilterX,
} from 'lucide-react-native';
import type { UserShort } from '@/common/api/models/userShort';
import { StudentEngagementRecord } from '../types';
import { useEngagementStore } from '../EngagementStore';
import TaskBoxes, { TaskBoxesLegend, calculateOverallProgress } from './TaskBoxes';

interface StudentGroup {
  student: UserShort;
  modules: StudentEngagementRecord[];
}

/**
 * JourneyMatrix Component
 * Human-centric monitoring hub. Grouped by student to show holistic progress.
 */
const JourneyMatrix: React.FC<{ groups: StudentGroup[] }> = ({ groups }) => {
  const [expandedStudents, setExpandedStudents] = useState<Set<string>>(new Set());
  const lastExpandedModuleId = useRef<string | undefined>(undefined);
  const {
    selectStudent, selectTask, filter, setFilter,
  } = useEngagementStore();

  // Auto-expand all matching students if a new module filter is applied
  useEffect(() => {
    if (filter.moduleId && groups.length > 0 && lastExpandedModuleId.current !== filter.moduleId) {
      setExpandedStudents(new Set(groups.map((g) => g.student.id)));
      lastExpandedModuleId.current = filter.moduleId;
    } else if (!filter.moduleId) {
      lastExpandedModuleId.current = undefined;
    }
  }, [filter.moduleId, groups]);

  const toggleExpand = (studentId: string) => {
    const next = new Set(expandedStudents);
    if (next.has(studentId)) next.delete(studentId);
    else next.add(studentId);
    setExpandedStudents(next);
  };

  const clearFilters = () => setFilter({
    moduleId: undefined,
    moduleVersion: undefined,
    workState: undefined,
  });

  if (groups.length === 0) {
    return (
      <Box
        p="$20"
        bg="$backgroundLight50"
        borderRadius="$xl"
        borderWidth={1}
        borderColor="$borderLight100"
        borderStyle="dashed"
      >
        <Center>
          <VStack space="md" alignItems="center">
            <Box p="$4" bg="$backgroundLight100" borderRadius="$full">
              <Icon as={FilterX} size="xl" color="$textHint" />
            </Box>
            <VStack space="xs" alignItems="center">
              <Heading size="sm" color="$textHeading">
                No students found
              </Heading>
              <Text size="sm" color="$textSubtle" textAlign="center">
                We couldn&apos;t find any students matching your current selection.
              </Text>
            </VStack>
            {filter.moduleId && (
              <Pressable onPress={clearFilters}>
                <Text size="sm" color="$primary600" fontWeight="semibold">
                  Clear all filters
                </Text>
              </Pressable>
            )}
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <VStack space="md">
      <HStack justifyContent="flex-end">
        <TaskBoxesLegend />
      </HStack>

      <VStack space="sm">
        {groups.map((group) => {
          const isExpanded = expandedStudents.has(group.student.id);
          const totalModules = group.modules.length;
          const hasStalled = group.modules.every((m) => m.workState === 'stalled');

          return (
            <Box
              key={group.student.id}
              borderWidth={1}
              borderColor={isExpanded ? '$primary300' : '$borderLight200'}
              borderRadius="$lg"
              overflow="hidden"
              bg="$white"
              softShadow="1"
            >
              {/* Student Header */}
              <Pressable onPress={() => toggleExpand(group.student.id)} testID={`student-row-${group.student.id}`}>
                <HStack
                  p="$4"
                  bg={isExpanded ? '$primary50' : '$white'}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <HStack alignItems="center" space="md" flex={1}>
                    <Icon as={isExpanded ? ChevronDown : ChevronRight} size="sm" color="$textHint" />
                    <Box p="$2" borderRadius="$full" bg={hasStalled ? '$error100' : '$backgroundLight100'}>
                      <Icon as={User} size="sm" color={hasStalled ? '$error600' : '$textSubtle'} />
                    </Box>
                    <VStack>
                      <Text size="md" fontWeight="bold" color="$textHeading">
                        {group.student.lastname}
                        ,
                        {' '}
                        {group.student.firstname}
                      </Text>
                      <Text size="xs" color="$textSubtle">
                        {group.student.email}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack space="md" alignItems="center">
                    {hasStalled && (
                      <Badge size="sm" action="warning" variant="outline">
                        <BadgeText size="2xs">&gt;30d INACTIVE</BadgeText>
                      </Badge>
                    )}
                    <Text size="xs" color="$textHint" fontWeight="medium">
                      {totalModules}
                      {' '}
                      Active
                      {totalModules === 1 ? 'Module' : 'Modules'}
                    </Text>
                  </HStack>
                </HStack>
              </Pressable>

              {/* Module Journey Details */}
              {isExpanded && (
                <VStack p="$4" space="lg" bg="$white" borderTopWidth={1} borderColor="$borderLight100">
                  <Text size="2xs" fontWeight="bold" color="$textHint" textTransform="uppercase" letterSpacing={1}>
                    Learning Journeys
                  </Text>
                  {group.modules.map((m) => (
                    <Box key={m.moduleId}>
                      <VStack
                        space="sm"
                        p="$3"
                        borderRadius="$md"
                        bg="$white"
                        borderWidth={1}
                        borderColor="$backgroundLight100"
                      >
                        <HStack justifyContent="space-between" alignItems="center">
                          <HStack space="xs" alignItems="baseline">
                            <Text size="sm" fontWeight="semibold" color="$textBody">
                              {m.moduleTitle}
                            </Text>
                            {m.moduleVersion && m.moduleVersion !== '0.0' && (
                            <Text size="2xs" color="$textSubtle">
                              v
                              {m.moduleVersion}
                            </Text>
                            )}
                          </HStack>
                          <HStack space="sm" alignItems="center">
                            {/* Sparkles icon removed - not needed for MVP */}
                            <Badge
                              size="sm"
                              variant="solid"
                              px="$1.5"
                              h={18}
                              bg={
                                  m.workState === 'stalled'
                                    ? '$warning600'
                                    : m.workState === 'submitted'
                                      ? '$success600'
                                      : '$backgroundLight700'
                                }
                            >
                              <BadgeText size="2xs" color="$white" textTransform="uppercase">
                                {m.workState}
                              </BadgeText>
                            </Badge>
                          </HStack>
                        </HStack>

                        {/* Task Progress Boxes - grouped by level */}
                        <VStack space="xs">
                          <HStack justifyContent="space-between" alignItems="center">
                            <Text size="2xs" color="$textSubtle">
                              Task Progress
                            </Text>
                            {m.levelProgress && Object.keys(m.levelProgress).length > 0 && (
                            <Text size="2xs" fontWeight="bold" color="$textSubtle">
                              {calculateOverallProgress(m.levelProgress)}
                              %
                            </Text>
                            )}
                          </HStack>
                          {m.levelProgress && Object.keys(m.levelProgress).length > 0 ? (
                            <TaskBoxes
                              levelProgress={m.levelProgress}
                              onTaskPress={(taskId) => {
                                selectStudent(group.student.id, m.courseId, m.moduleId, m.moduleTitle);
                                selectTask(taskId);
                              }}
                            />
                          ) : (
                            <Text size="2xs" color="$textHint" italic>
                              No task data available
                            </Text>
                          )}
                        </VStack>

                        <HStack justifyContent="space-between" alignItems="center" mt="$1">
                          <Text size="2xs" color="$textHint">
                            Last activity:
                            {' '}
                            {m.lastActivity ? new Date(m.lastActivity as string).toLocaleDateString() : 'Never'}
                          </Text>
                          <HStack alignItems="center" space="xs">
                            <Text size="2xs" color="$textHint">
                              Click a task box to inspect draft
                            </Text>
                          </HStack>
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          );
        })}
      </VStack>
    </VStack>
  );
};

export default JourneyMatrix;
